import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { ApiError } from '../../api/problem'
import { useCampaignSheet } from '../../domains/campaigns/queries'
import { useCaseList, useCreateCase } from '../../domains/cases/queries'
import { ArcaneCircle, Banner, Button, Card, Input, SectionLabel } from '../../design/components'
import styles from './play.module.css'

/**
 * A campaign's cases — the stories running through it.
 *
 * Any member may read the list; only the Game Master creates one, and that is the
 * campaign's own `isGameMaster` flag rather than anything re-derived here.
 */
export function CaseListPage() {
  const params = useParams<{ id: string }>()
  const campaignId = Number(params.id)

  const [creating, setCreating] = useState(false)
  const query = useCaseList(campaignId)
  const campaign = useCampaignSheet(campaignId)

  const cases = query.data?.items ?? []
  const isGameMaster = campaign.data?.isGameMaster === true
  const campaignName = campaign.data?.sections.core?.name

  return (
    <AppShell title="Cases" subtitle={campaignName?.toUpperCase() ?? 'THE STORIES IN PLAY'}>
      {query.isPending && <Card>Loading…</Card>}

      {query.isError && (
        <Card>
          <Banner tone={notFound(query.error) ? 'info' : 'error'} urgent={!notFound(query.error)}>
            {notFound(query.error)
              ? 'No such campaign, or it is not one of yours.'
              : 'Could not load this campaign’s cases.'}
          </Banner>
        </Card>
      )}

      {query.isSuccess && cases.length === 0 && !creating && (
        <Card label="No cases yet">
          <p className={styles.note}>
            {isGameMaster
              ? 'A case is one story: its major characters, and the scenes it runs through.'
              : 'The Game Master has not opened a case in this campaign yet.'}
          </p>
        </Card>
      )}

      {cases.length > 0 && (
        <ul className={styles.list}>
          {cases.map((entry) => (
            <li key={entry.id}>
              <Link to={`/cases/${entry.id}`} className={styles.row}>
                <ArcaneCircle size={36} className={styles.sigil} />
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{entry.name}</span>
                  <span className={styles.rowMeta}>
                    {[
                      `${entry.sceneCount ?? 0} ${entry.sceneCount === 1 ? 'scene' : 'scenes'}`,
                      `${entry.majorCharacterCount ?? 0} major`,
                    ].join(' · ')}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {isGameMaster &&
        (creating ? (
          <CreateCaseCard campaignId={campaignId} onDone={() => setCreating(false)} />
        ) : (
          <Button fullWidth onClick={() => setCreating(true)}>
            New case
          </Button>
        ))}

      <Link to={`/campaigns/${campaignId}`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          Back to the campaign
        </Button>
      </Link>
    </AppShell>
  )
}

function notFound(error: unknown): boolean {
  return error instanceof ApiError && (error.kind === 'not-found' || error.kind === 'forbidden')
}

function CreateCaseCard({ campaignId, onDone }: { campaignId: number; onDone: () => void }) {
  const navigate = useNavigate()
  const create = useCreateCase(campaignId)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      const sheet = await create.mutateAsync({
        name,
        ...(description.trim() === '' ? {} : { description }),
      })
      void navigate(`/cases/${sheet.caseId}`)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not create the case.')
    }
  }

  return (
    <Card variant="fate" label="New case">
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <Banner tone="error" urgent>
            {error}
          </Banner>
        )}

        <Input
          label="Name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            Description
          </SectionLabel>
          <textarea
            className={styles.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onDone} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || name.trim() === ''}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
